import { useState, useRef, useLayoutEffect } from 'react';

/**
 * A reusable tab selector with an animated sliding underline.
 *
 * @param {Object} props
 * @param {Array<Object>} props.tabs
 * @param {string} props.tabs[].label
 * @param {React.ReactNode} [props.tabs[].icon]
 * @param {number} [props.initialTab=0]
 * @param {(tabIndex: number) => void} [props.onChange]
 */
export default function TabSelector({
  tabs,
  initialTab = 0,
  onChange,
}) {
  const [selectedTab, setSelectedTab] = useState(initialTab);

  const [indicator, setIndicator] = useState({
    left: 0,
    width: 0,
  });

  const containerRef = useRef(null);
  const tabRefs = useRef([]);

  // Update underline position whenever the selected tab changes.
  useLayoutEffect(() => {
    const updateIndicator = () => {
      const tab = tabRefs.current[selectedTab];
      const container = containerRef.current;

      if (!tab || !container) return;

      const tabRect = tab.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      setIndicator({
        left: tabRect.left - containerRect.left,
        width: tabRect.width,
      });
    };

    updateIndicator();

    // Keep underline aligned when tabs resize.
    const observer = new ResizeObserver(updateIndicator);

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    tabRefs.current.forEach((tab) => {
      if (tab) observer.observe(tab);
    });

    return () => observer.disconnect();
  }, [selectedTab, tabs]);

  const handleChange = (tabIndex) => {
    if (tabIndex === selectedTab) return;

    setSelectedTab(tabIndex);
    onChange?.(tabIndex);
  };

  return (
    <div
      ref={containerRef}
      className="relative flex border-b border-gray-700"
    >
      {tabs.map((tab, index) => {
        const isSelected = selectedTab === index;

        return (
          <button
            key={index}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            onClick={() => handleChange(index)}
            className={[
              "relative flex items-center gap-2 px-2.5 sm:px-5 py-3",
              "text-sm font-medium",
              "transition-colors duration-200",
              "focus:outline-none",
              isSelected
                ? "text-white"
                : "text-gray-400 hover:text-gray-200",
            ].join(" ")}
          >
            {tab.icon && (
              <span className="flex items-center">
                {tab.icon}
              </span>
            )}

            <span className='whitespace-nowrap'>{tab.label}</span>
          </button>
        );
      })}

      {/* Animated sliding underline */}
      <span
        className="
          pointer-events-none
          absolute bottom-0
          h-0.5
          bg-[#f9a928]
          transition-[left,width]
          duration-300
          ease-in-out
          motion-reduce:transition-none
        "
        style={{
          left: indicator.left,
          width: indicator.width,
        }}
      />
    </div>
  );
}
